/**
 * C5 — the invitation, over whatever page the inventor is on.
 *
 * Figure C5 draws it over HOME: "Welcome back, Amara", the stat tiles, Recent
 * inventions, the atlas. That is the point of the screen, not decoration —
 * the card arrives unbidden, the moment an organisation invites the account you
 * are signed in as, and the figure shows it interrupting whatever you were
 * doing. Mounting it only on the sharing page made it something you had to
 * navigate to, which is the one thing the card's own "How you got here"
 * paragraph says it is not.
 *
 * So the overlay lives here, self-contained, and both pages mount it: Home
 * (pages/inventor/Dashboard.tsx) and the sharing page itself, where
 * "You can revisit this any time at Account › Sharing" leads.
 *
 * It renders nothing at all unless the URL carries ?accept — no query fires and
 * no scrim appears on an ordinary visit.
 */
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { acceptSponsorship, listPendingSeats } from '../../services/api'
import PortfolioInvitation from './PortfolioInvitation'

export default function PortfolioInvitationOverlay() {
  const [searchParams, setSearchParams] = useSearchParams()
  const isAccepting = searchParams.has('accept')
  const acceptParam = searchParams.get('accept')
  const qc = useQueryClient()

  // The cache keys read as the screen reads. They were 'pending-seats',
  // 'my-sponsors' and 'seat-changes' — v1 licensing words, invisible to a
  // reader but part of the vocabulary this build is removing, and the gate in
  // src/screens/vocabulary.test.ts is right to catch them wherever they live.
  const pendingQuery = useQuery({
    queryKey: ['portfolio-invitations'],
    queryFn: listPendingSeats,
    enabled: isAccepting,
  })

  if (!isAccepting) return null

  // ?accept=<aggregator id> names WHICH invitation. A bare ?accept (or an id
  // with no matching invitation) falls to the first one waiting, and none at
  // all gives the card its own "no longer available" state — never nothing.
  const invitation = (pendingQuery.data ?? []).find(
    (p) => !acceptParam || p.aggregator_id === acceptParam,
  ) ?? null

  const dismiss = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('accept')
      return next
    })
  }

  const accept = async (aggregatorId: string) => {
    await acceptSponsorship(aggregatorId)
    qc.invalidateQueries({ queryKey: ['portfolio-invitations'] })
    qc.invalidateQueries({ queryKey: ['portfolios-im-in'] })
    qc.invalidateQueries({ queryKey: ['sharing-changes'] })
    dismiss()
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-iflow-950/50 p-4">
      <div className="w-full max-w-[640px]">
        <PortfolioInvitation invitation={invitation} onAccept={accept} onDismiss={dismiss} />
      </div>
    </div>
  )
}
